/*
 * DSD Moderation Metadata Foundation - p11dz144
 * Governance-only moderation metadata contract for canonical social objects.
 * Defines moderation states, content ratings, sensitivity flags, review flags,
 * audit metadata, and future universe/galaxy inheritance without enabling
 * Firebase reads, Firebase writes, realtime listeners, moderation actions, or enforcement.
 */
(function(){
  'use strict';
  if (window.DSDModerationMetadata && window.DSDModerationMetadata.version) return;

  var VERSION = 'p11dz144-moderation-metadata-foundation-v1';
  var OWNER = 'ModerationMetadata';
  var FIREBASE_GATE = 'disabled-moderation-metadata-governance-only';

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }

  var contentRatings = {
    safe: {
      key: 'safe',
      label: 'Safe',
      description: 'General social content eligible for normal discovery and feed surfaces.',
      adultGateRequired: false,
      sensitiveGateRequired: false,
      defaultDiscoveryEligible: true
    },
    sensitive: {
      key: 'sensitive',
      label: 'Sensitive',
      description: 'Content that may require reduced recommendation, warning labels, or additional review.',
      adultGateRequired: false,
      sensitiveGateRequired: true,
      defaultDiscoveryEligible: false
    },
    mature: {
      key: 'mature',
      label: 'Mature',
      description: 'Mature but not explicit content. Requires mature-content aware surfaces.',
      adultGateRequired: false,
      sensitiveGateRequired: true,
      defaultDiscoveryEligible: false
    },
    adult: {
      key: 'adult',
      label: 'Adult',
      description: 'Adult/NSFW ecosystem content. Requires adult gate and strict moderation handling.',
      adultGateRequired: true,
      sensitiveGateRequired: true,
      defaultDiscoveryEligible: false
    },
    restricted: {
      key: 'restricted',
      label: 'Restricted',
      description: 'Platform-restricted content not eligible for public discovery until reviewed.',
      adultGateRequired: true,
      sensitiveGateRequired: true,
      defaultDiscoveryEligible: false,
      requiresReview: true
    }
  };

  var moderationStates = {
    clean: {
      key: 'clean',
      label: 'Clean',
      renderable: true,
      interactionAllowed: true,
      reviewRequired: false
    },
    flagged: {
      key: 'flagged',
      label: 'Flagged',
      renderable: true,
      interactionAllowed: true,
      reviewRequired: true
    },
    underReview: {
      key: 'underReview',
      label: 'Under Review',
      renderable: false,
      interactionAllowed: false,
      reviewRequired: true
    },
    hidden: {
      key: 'hidden',
      label: 'Hidden',
      renderable: false,
      interactionAllowed: false,
      reviewRequired: true
    },
    locked: {
      key: 'locked',
      label: 'Locked',
      renderable: true,
      interactionAllowed: false,
      reviewRequired: false
    },
    removed: {
      key: 'removed',
      label: 'Removed',
      renderable: false,
      interactionAllowed: false,
      reviewRequired: false,
      auditOnly: true
    }
  };

  var reviewFlags = {
    spam: 'Spam / automation risk',
    harassment: 'Harassment or targeted abuse risk',
    impersonation: 'Impersonation / identity concern',
    copyright: 'Copyright / media ownership concern',
    consent: 'Consent or non-consensual content concern',
    illegalContent: 'Illegal content concern requiring platform escalation',
    adultMisclassification: 'Adult/sensitive content may be misclassified',
    underageRisk: 'Age / underage risk escalation',
    paymentOrScam: 'Payment, solicitation, scam, or fraud concern',
    violenceThreat: 'Violence/threat/safety concern',
    selfHarm: 'Self-harm/safety concern',
    platformPolicy: 'General platform policy review'
  };

  var objectDefaults = {
    post: {
      contentRating: 'safe',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: null
    },
    comment: {
      contentRating: 'inherits-parent-post',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: 'post'
    },
    reaction: {
      contentRating: 'inherits-parent-post',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: 'post'
    },
    story: {
      contentRating: 'safe',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: 'story-scope'
    },
    profile: {
      contentRating: 'safe',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: null
    },
    universe: {
      contentRating: 'safe',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: null
    },
    galaxy: {
      contentRating: 'safe',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: 'universe-policy-when-present'
    },
    galaxyChatMessage: {
      contentRating: 'inherits-galaxy',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: 'galaxy'
    },
    event: {
      contentRating: 'safe',
      moderationState: 'clean',
      reviewFlags: [],
      inheritsFrom: 'event-scope'
    }
  };

  var inheritanceDoctrine = {
    post: ['explicit-post-rating', 'surface-default', 'platform-default'],
    comment: ['parent-post-rating', 'explicit-comment-escalation', 'platform-default'],
    reaction: ['parent-post-rating', 'platform-default'],
    story: ['story-scope-rating', 'explicit-story-rating', 'platform-default'],
    galaxy: ['explicit-galaxy-rating', 'universe-policy', 'platform-default'],
    galaxyChatMessage: ['galaxy-rating', 'explicit-message-escalation', 'platform-default'],
    universe: ['explicit-universe-rating', 'platform-default'],
    event: ['event-scope-rating', 'explicit-event-rating', 'platform-default']
  };

  var moderationDoctrine = [
    'Moderation metadata is attached to canonical social objects; it does not create a separate renderer, feed, modal, or storage architecture.',
    'Content ratings and moderation states must be available before realtime writes are enabled.',
    'Adult/sensitive classification must propagate to posts, comments, stories, galaxy chat, events, universes, and galaxies unless platform governance overrides it.',
    'Moderation states override visibility eligibility but do not own visibility logic.',
    'Report and enforcement actions are future systems; this build only defines metadata and audit shape.',
    'Future universe and galaxy founders/admins/moderators may manage scoped moderation, but platform governance remains the final authority.',
    'No Firebase reads, writes, realtime listeners, report writes, or enforcement actions are enabled by this layer.'
  ];

  function normalizeRating(value, fallback){
    if (!value) return fallback || 'safe';
    if (contentRatings[value]) return value;
    if (String(value).indexOf('inherits-') === 0) return value;
    return fallback || 'safe';
  }

  function normalizeState(value, fallback){
    if (!value) return fallback || 'clean';
    return moderationStates[value] ? value : (fallback || 'clean');
  }

  function normalizeFlags(flags){
    if (!Array.isArray(flags)) return [];
    var seen = {};
    return flags.filter(function(flag){
      var key = String(flag || '').trim();
      if (!key || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function getDefaults(objectType){
    return objectDefaults[objectType] || objectDefaults.post;
  }

  function normalizeMetadata(object, context){
    object = object || {};
    context = context || {};
    var objectType = object.objectType || object.type || context.objectType || 'post';
    var defaults = getDefaults(objectType);
    var moderation = object.moderation || {};
    var rating = normalizeRating(object.contentRating || moderation.contentRating || context.contentRating, defaults.contentRating);
    var state = normalizeState(object.moderationState || object.reviewState || moderation.state || context.moderationState, defaults.moderationState);
    var flags = normalizeFlags([].concat(defaults.reviewFlags || [], object.reviewFlags || [], moderation.reviewFlags || [], context.reviewFlags || []));
    var stateConfig = moderationStates[state] || moderationStates.clean;
    var ratingConfig = contentRatings[rating] || null;
    var visibilityDecision = null;

    try {
      if (window.DSDVisibilityLayer && typeof window.DSDVisibilityLayer.evaluateVisibility === 'function') {
        visibilityDecision = window.DSDVisibilityLayer.evaluateVisibility(Object.assign({}, object, {
          moderationState: state,
          contentRating: rating
        }), context);
      }
    } catch(e) {}

    return {
      version: VERSION,
      objectType: objectType,
      objectId: object.postId || object.commentId || object.storyId || object.id || object.objectId || null,
      contentRating: rating,
      moderationState: state,
      reviewFlags: flags,
      reviewRequired: !!(stateConfig.reviewRequired || (ratingConfig && ratingConfig.requiresReview) || flags.length),
      renderableByModeration: !!stateConfig.renderable,
      interactionAllowedByModeration: stateConfig.interactionAllowed !== false,
      adultGateRequired: !!(ratingConfig && ratingConfig.adultGateRequired),
      sensitiveGateRequired: !!(ratingConfig && ratingConfig.sensitiveGateRequired),
      inheritance: inheritanceDoctrine[objectType] || inheritanceDoctrine.post,
      inheritsFrom: defaults.inheritsFrom,
      visibilityDecision: visibilityDecision,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementMode: 'metadata-contract-only'
    };
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      owner: OWNER,
      firebaseGate: FIREBASE_GATE,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementEnabled: false,
      doctrine: moderationDoctrine,
      contentRatings: contentRatings,
      moderationStates: moderationStates,
      reviewFlags: reviewFlags,
      objectDefaults: objectDefaults,
      inheritanceDoctrine: inheritanceDoctrine,
      dependencyReadiness: {
        DSDRegistry: exists('DSDRegistry'),
        VisibilityLayer: exists('DSDVisibilityLayer'),
        SurfaceRegistry: exists('DSDSurfaceRegistry'),
        FirebaseSocialSchema: exists('DSDFirebaseSocialSchema'),
        RealtimeOwnershipAudit: exists('DSDRealtimeOwnershipAudit'),
        CommentAuthorityAudit: exists('DSDCommentAuthorityAudit'),
        ReactionAuthorityAudit: exists('DSDReactionAuthorityAudit'),
        NotificationDependencyGraph: exists('DSDNotificationDependencyGraph'),
        firebaseReadsEnabled: false,
        firebaseWritesEnabled: false,
        realtimeEnabled: false
      },
      nextGate: 'p11dz145-classification-layer-or-controlled-read-only-post-activation-gate'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('moderation-metadata:doctrine', { doctrine: moderationDoctrine, count: moderationDoctrine.length });
    claim('moderation-metadata:content-ratings', { ratings: contentRatings, count: Object.keys(contentRatings).length });
    claim('moderation-metadata:states', { states: moderationStates, count: Object.keys(moderationStates).length });
    claim('moderation-metadata:review-flags', { flags: reviewFlags, count: Object.keys(reviewFlags).length });
    claim('moderation-metadata:object-defaults', objectDefaults);
    claim('moderation-metadata:inheritance-doctrine', inheritanceDoctrine);
    claim('moderation-metadata:dependency-readiness', report.dependencyReadiness);
    claim('moderation-metadata:firebase-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementEnabled: false,
      gate: FIREBASE_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:moderation-metadata-ready', { detail: report })); } catch(e) {}
    window.DSDModerationMetadataReport = report;
    return report;
  }

  window.DSDModerationMetadata = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    contentRatings: contentRatings,
    moderationStates: moderationStates,
    reviewFlags: reviewFlags,
    objectDefaults: objectDefaults,
    inheritanceDoctrine: inheritanceDoctrine,
    normalizeRating: normalizeRating,
    normalizeState: normalizeState,
    normalizeFlags: normalizeFlags,
    normalizeMetadata: normalizeMetadata
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
